import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Dropdown } from 'antd';
import type { ItemType } from 'antd/lib/menu/hooks/useItems';
import { AssetItemDto } from '@api/Api';
import { useCopyAssetsMutation } from '@hooks/queries/assets';
import {
  deleteAssetAction,
  restoreDeletedAssetAction
} from '@redux/actions/assetsAction';
import {
  toggleShareAssetModal,
  toggleApprovalModal,
  toggleCopyModal,
  toggleMoveModal,
  toggleAddToExistingLinkModal
} from '@redux/actions/modalAction';
import { useCurrentWorkspace } from '@hooks/workspace';
import { downloadAll } from '@helpers/download';
import { useOrganization } from '@components/OrganizationBoundary';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import useTypedDispatch from '@hooks/useTypedDispatch';
import {
  ActionIcon,
  ApprovalRoutingIcon,
  DownloadInvoiceIcon,
  DeleteIcon,
  DiamondUpgradeIcon,
  DropdownCopyIcon,
  DropdownDuplicateIcon,
  DropdownLinkIcon,
  DropdownLinkPlusIcon
} from '@icons';
import './BulkActions.less';

type BulkActionsProps = {
  selectedAssets: { node: AssetItemDto; cursor: string }[];
  canShare: boolean;
  onClose: () => void;
  page: string;
  onRefreshAssetList?: () => void;
};

function BulkActions({
  selectedAssets,
  canShare,
  onClose,
  page,
  onRefreshAssetList
}: BulkActionsProps) {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const [currentWorkspace] = useCurrentWorkspace(false);
  const canCreateAw = !!currentWorkspace?.permissions.createApprovalWorkflows;
  const { currentOrganization } = useOrganization();
  const awFeatureEnabled =
    !!currentOrganization.entity?.features.approvalRouting;

  const { mutateAsync: copyAssets, isPending: isCopyingAssets } =
    useCopyAssetsMutation({});

  const canBulkMove = useMemo(() => {
    if (!selectedAssets.length) return false;
    return selectedAssets
      .map(({ node }) => node.campaignId)
      .every((it, _idx, array) => it === array[0]);
  }, [selectedAssets]);

  const { canBulkDelete, canBulkDownload, canBulkApprovalWorkflow } =
    useMemo(() => {
      if (!selectedAssets.length)
        return {
          canBulkDownload: false,
          canBulkDelete: false,
          canBulkShare: false,
          canBulkApprovalWorkflow: false
        };

      const canBulkDownload: boolean = selectedAssets.every(
        (asset: any) => asset?.node?.asset?.permissions.download
      );
      const canBulkDelete: boolean = selectedAssets.every(
        (asset: any) => asset?.node?.asset?.permissions.delete
      );

      const canBulkApprovalWorkflow = selectedAssets.every(
        (asset: { node: AssetItemDto; cursor: string }) =>
          !asset?.node?.asset?.approvalWorkflow
      );

      return {
        canBulkDownload,
        canBulkDelete,
        canBulkApprovalWorkflow
      };
    }, [selectedAssets]);

  const { length } = selectedAssets;
  const upgradeShare =
    length > 1 && !currentOrganization.entity?.features.shareMultipleAssets;
  const canUseBatchApprovals =
    !!currentOrganization.entity?.features.batchApprovals || length === 1;

  const createApprovalRoutingTooltipText = useMemo(() => {
    if (!awFeatureEnabled) {
      return ' to a paid plan to access this feature.';
    }
    if (!canUseBatchApprovals) {
      return ' to an Enterprise plan to route multiple media assets for approval at one time.';
    }
    if (!canBulkApprovalWorkflow) {
      return length === 1
        ? 'Approval routing is active for the selected file.'
        : 'Approval routing is active for some of the selected files.';
    }
    return '';
  }, [awFeatureEnabled, canUseBatchApprovals, canBulkApprovalWorkflow, length]);

  const menuItems: ItemType[] = [
    {
      key: 'duplicate-folder',
      label: 'Duplicate',
      disabled: isCopyingAssets,
      icon: <DropdownDuplicateIcon />,
      onClick: () => {
        if (!selectedAssets?.length) return;
        copyAssets({
          workspaceId: selectedAssets[0].node.workspaceId,
          assetIds: selectedAssets.map((asset) => asset.node.id),
          targetCampaignId: selectedAssets[0].node.campaignId,
          targetFolderId: selectedAssets[0].node.path.slice(-1)[0]?.id,
          copyComments: true
        });
      }
    },
    ...(canBulkMove
      ? [
          {
            key: 'move_to',
            label: 'Move to',
            icon: <DropdownCopyIcon />,
            onClick: () => {
              dispatch(
                toggleMoveModal({
                  visible: true,
                  type: 'assets',
                  assets: selectedAssets.map((a) => ({
                    id: a.node.id,
                    name: `${a.node.asset?.versions[0]?.name}.${a.node.asset?.versions[0]?.extension}`
                  })),
                  campaignId: selectedAssets[0].node.campaignId,
                  campaignName: selectedAssets[0].node.campaignName,
                  permissions: {
                    moveToCampaign: selectedAssets.every(
                      (a) => a.node.asset?.permissions.moveToCampaign
                    ),
                    moveToPublicFolder: selectedAssets.every(
                      (a) => a.node.asset?.permissions.moveToPublicFolder
                    ),
                    moveToPrivateFolder: selectedAssets.every(
                      (a) => a.node.asset?.permissions.moveToPrivateFolder
                    )
                  }
                })
              );
            }
          }
        ]
      : []),
    ...(!canShare || upgradeShare
      ? []
      : [
          {
            type: 'divider' as const
          },
          {
            key: 'add-to-existing-link',
            label: 'Add to existing link',
            icon: <DropdownLinkIcon />,
            onClick: () => {
              dispatch(
                toggleAddToExistingLinkModal({
                  visible: true,
                  campaignId: selectedAssets[0].node.campaignId,
                  assets: selectedAssets.map((asset) => ({
                    id: asset.node.id,
                    versionId: asset.node.asset?.versions[0]?.id
                  }))
                })
              );
            }
          }
        ]),

    ...(canBulkDelete
      ? [
          {
            type: 'divider' as const
          },
          {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteIcon />,
            onClick: () => {
              if (!canBulkDelete) return;
              for (const asset of selectedAssets) {
                dispatch(
                  deleteAssetAction(
                    { assetId: asset.node.id, permanent: false },
                    asset.node.path.slice(-1)[0]?.id
                  )
                );
              }
            }
          }
        ]
      : [])
  ];

  return (
    <div
      className={
        length > 0
          ? 'bulk_actions_container bulk_actions_container_active'
          : 'bulk_actions_container'
      }
    >
      <div className="bulk_actions_container__body">
        <div className="items">
          <div className="list" />
          <div className="copy">{length} media selected</div>
        </div>
        <div className="close" onClick={onClose} />
      </div>
      <div className="bulk_actions_container__actions">
        {page === 'deleted' ? (
          <>
            <Button
              type="text"
              size="small"
              className="action"
              disabled={!canBulkDelete}
              onClick={async () => {
                if (!canBulkDelete) return;
                for (const asset of selectedAssets) {
                  await dispatch(
                    deleteAssetAction(
                      {
                        assetId: asset.node.id,
                        permanent: true
                      },
                      asset.node.path.slice(-1)[0]?.id
                    )
                  );
                }
                onClose();
              }}
            >
              <DeleteIcon /> Delete forever
            </Button>
            <Button
              type="text"
              size="small"
              className="action"
              disabled={!canBulkDelete}
              onClick={async () => {
                if (!canBulkDelete) return;
                for (const asset of selectedAssets) {
                  await dispatch(
                    restoreDeletedAssetAction({ assetId: asset.node.id })
                  );
                }
                onClose();
              }}
            >
              Restore
            </Button>
          </>
        ) : (
          <>
            <Dropdown
              trigger={['click']}
              overlayClassName="asset_links_overlay"
              menu={{
                items: menuItems,
                onClick: () => {
                  onClose();
                }
              }}
            >
              <Button type="text" size="small" className="action">
                <ActionIcon className="action__icon" />
                More
              </Button>
            </Dropdown>
            <UpgradePlanTooltip
              text=" to a Team plan to access this feature."
              placement="left"
              isCanVisible={upgradeShare}
            >
              <Button
                type="text"
                size="small"
                className="action"
                disabled={!canShare || upgradeShare}
                onClick={() => {
                  const assets = selectedAssets.map((it: any) => ({
                    id: it.node.id,
                    versionId: it.node.asset?.versions[0]?.id
                  }));
                  dispatch(
                    toggleShareAssetModal({
                      visible: true,
                      assets,
                      campaignId: selectedAssets[0].node.campaignId
                    })
                  );
                  onClose();
                }}
              >
                {upgradeShare && (
                  <DiamondUpgradeIcon className="action__icon upgrade-icon" />
                )}
                {!upgradeShare && (
                  <DropdownLinkPlusIcon className="action__icon" />
                )}
                New share link
              </Button>
            </UpgradePlanTooltip>
            <Button
              type="text"
              size="small"
              className="action"
              onClick={() => {
                if (!selectedAssets?.length) return;
                dispatch(
                  toggleCopyModal({
                    visible: true,
                    type: 'assets',
                    assets: selectedAssets.map((asset) => ({
                      id: asset.node.id,
                      name: `${asset.node.asset?.versions[0]?.name}.${asset.node.asset?.versions[0].extension}`
                    }))
                  })
                );
                onClose();
              }}
            >
              <DropdownCopyIcon className="action__icon" />
              Copy to
            </Button>
            <UpgradePlanTooltip
              text={createApprovalRoutingTooltipText}
              placement="top"
              isCanVisible={!!createApprovalRoutingTooltipText}
              isShowUpgradeText={!canUseBatchApprovals || !awFeatureEnabled}
            >
              <Button
                type="text"
                size="small"
                className="action"
                disabled={!!createApprovalRoutingTooltipText}
                onClick={() => {
                  if (
                    !canUseBatchApprovals ||
                    !canBulkApprovalWorkflow ||
                    !awFeatureEnabled
                  )
                    return;
                  const campaignId = selectedAssets?.[0]?.node?.campaignId;
                  if (!campaignId) return;
                  const assetVersions = selectedAssets
                    .filter((item) => !!item.node.asset?.versions[0])
                    .map((item) => ({
                      ...item.node.asset!.versions[0],
                      assetId: item.node.id
                    }));
                  dispatch(
                    toggleApprovalModal({
                      visible: true,
                      assetVersions,
                      campaignId,
                      permissions: {
                        create: canCreateAw
                      },
                      callback: (aw) => {
                        onClose();
                        if (aw) {
                          navigate(
                            `/campaigns/approvals/${aw.workspaceId}/${aw.campaignId}/${aw.id}`
                          );
                          return;
                        }
                        onRefreshAssetList?.();
                      }
                    })
                  );
                }}
              >
                {!canUseBatchApprovals || !awFeatureEnabled ? (
                  <DiamondUpgradeIcon className="action__icon upgrade-icon" />
                ) : (
                  <ApprovalRoutingIcon className="action__icon" />
                )}
                Create approval routing
              </Button>
            </UpgradePlanTooltip>

            <Button
              type="text"
              size="small"
              className="action"
              disabled={!canBulkDownload}
              onClick={() => {
                if (!canBulkDownload) return;
                downloadAll(
                  selectedAssets.map((x) => ({
                    ...x.node,
                    path: []
                  }))
                );
                onClose();
              }}
            >
              <DownloadInvoiceIcon className="action__icon" />
              Download
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default BulkActions;
